import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Table, Nav, Tab, Badge, OverlayTrigger, Tooltip, Button, Dropdown, ListGroup } from 'react-bootstrap';
import { Line, Pie, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import { agentApi, notificationApi, searchApi } from '../../services/api';
import { 
  FaRupeeSign, 
  FaPercentage, 
  FaUsers, 
  FaStar, 
  FaArrowUp, 
  FaArrowDown,
  FaChartBar,
  FaChartPie,
  FaUserFriends,
  FaCreditCard,
  FaInfoCircle,
  FaFileInvoiceDollar,
  FaQuestionCircle,
  FaUser,
  FaSignOutAlt,
  FaSearch,
  FaTimes,
  FaBell
} from 'react-icons/fa';
import AgentSidebar from '../../components/AgentSidebar';
import SearchResults from '../../components/SearchResults';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './Earnings.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  Filler
);

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Helper function to format percentages
const formatPercentage = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(2)}%`;
};

// Gradient helper for charts
const createGradient = (ctx, colorStart, colorEnd) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, 400);
  gradient.addColorStop(0, colorStart);
  gradient.addColorStop(1, colorEnd);
  return gradient;
};

const AgentEarnings = () => {
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();
  
  const [summaryStats, setSummaryStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeKey, setActiveKey] = useState('overview');
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Search handlers
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim().length >= 2) {
      // Set a new timeout for search
      const timeout = setTimeout(() => {
        performSearch(query);
      }, 500); // Debounce 500ms
      setSearchTimeout(timeout);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };
  
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };
  
  // Notification handlers
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };
  
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; }
  };
  
  useEffect(() => {
    document.title = 'Agent Earnings Dashboard';

    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // Using allSettled to handle partial API failures gracefully
        const results = await Promise.allSettled([
          agentApi.getEarningsStats(),
          agentApi.getMonthlyEarnings(),
          agentApi.getEarningsByProduct(),
        ]);
        
        // Extract data from fulfilled promises or set defaults for rejected ones
        const [statsRes, monthlyRes, productRes] = results;
        
        // Summary Stats
        if (statsRes.status === 'fulfilled') {
          setSummaryStats(statsRes.value.data);
        } else {
          console.error('Error fetching summary stats:', statsRes.reason);
          setSummaryStats({
            total_earnings: 0,
            pending_payouts: 0,
            average_commission: 0,
            top_product_name: 'N/A',
            total_leads: 0
          });
        }
        
        // Monthly Data
        if (monthlyRes.status === 'fulfilled') {
          setMonthlyData(monthlyRes.value.data);
        } else {
          console.error('Error fetching monthly data:', monthlyRes.reason);
          setMonthlyData({
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
            datasets: [{ data: [0, 0, 0, 0, 0, 0] }]
          });
        }
        
        // Product Data
        if (productRes.status === 'fulfilled') {
          // Check if data is structured with 'items' property
          const items = productRes.value.data.items || productRes.value.data;
          setProductData(items);
        } else {
          console.error('Error fetching product data:', productRes.reason);
          setProductData([]);
        }
        
        // Check if all APIs failed and set an error if so
        const allFailed = results.every(result => result.status === 'rejected');
        if (allFailed) {
          setError('Could not load any earnings data. Please check your connection and try again.');
        }
      } catch (err) {
        console.error("Error fetching earnings data:", err);
        setError(err.response?.data?.detail || 'Failed to load earnings data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  
  // Notifications polling effect
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute
    
    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]);

  // Handle clicks outside notification dropdown and search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Chart Options & Data ---

  // Monthly Trend Line Chart
  // Convert the function to a usable object for react-chartjs-2 v4+
  const getMonthlyChartData = {
    labels: monthlyData?.labels || [],
    datasets: [
      {
        label: 'Monthly Earnings',
        data: monthlyData?.datasets?.[0]?.data || [],
        borderColor: '#3b82f6',
        borderWidth: 2,
        backgroundColor: 'rgba(59, 130, 246, 0.2)',  // Simple background color instead of gradient
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: '#3b82f6',
        pointHoverBorderWidth: 2,
        pointHoverBorderColor: '#fff',
      },
    ],
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(17, 25, 40, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            return ` Earnings: ${formatCurrency(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
          },
          color: '#64748b',
          padding: 8,
        }
      },
      y: {
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: '#f1f5f9',
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
          },
          color: '#64748b',
          padding: 8,
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    elements: {
      line: {
        borderWidth: 2,
      }
    }
  };

  // Product Doughnut Chart
  const productChartData = {
    labels: productData?.map(item => item?.category_name || 'Unknown') || [],
    datasets: [
      {
        data: productData?.map(item => item?.total_amount || 0) || [],
        backgroundColor: [
          '#3b82f6', // Blue
          '#6366f1', // Indigo
          '#8b5cf6', // Purple
          '#ec4899', // Pink
          '#f43f5e', // Rose
          '#f97316', // Orange
          '#f59e0b', // Amber
          '#10b981', // Emerald
          '#06b6d4', // Cyan
          '#0ea5e9', // Light Blue
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const productChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          padding: 20,
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          font: { 
            size: 13,
            weight: '500' 
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 25, 40, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return ` ${context.label}: ${formatCurrency(value)} (${percentage}%)`;
          }
        }
      }
    },
  };

  // --- Render Logic ---

  if (loading) {
    return (
      <div className="agent-dashboard-layout">
        <AgentSidebar />
        <div className="agent-content-wrapper">
          <header className="agent-header">
            <div className="agent-greeting">
              <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
            </div>

            <div ref={searchRef} className="agent-header-search">
              <div className="search-input-container">
                <FaSearch className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search leads, products, invoices..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (searchResults.length > 0) {
                      setShowSearchResults(true);
                    }
                  }}
                />
                {searchQuery && (
                  <button className="clear-search-btn" onClick={clearSearch}>
                    <FaTimes />
                  </button>
                )}
                {isSearching && (
                  <Spinner 
                    animation="border" 
                    size="sm" 
                    className="search-spinner" 
                  />
                )}
              </div>
              
              {/* Search results dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="search-results-dropdown">
                  <SearchResults 
                    results={searchResults}
                    onResultClick={() => setShowSearchResults(false)}
                  />
                </div>
              )}
            </div>

            <div className="agent-header-actions">
              <div className="agent-header-icon">
                <FaQuestionCircle size={18} />
              </div>
              
              <div ref={notificationRef} className="position-relative agent-notifications me-3">
                <Button
                  variant="link"
                  className="text-secondary p-0 position-relative"
                  onClick={toggleNotifications}
                  id="dropdown-notifications-agent"
                  aria-expanded={showNotifications}
                  title="Notifications"
                >
                  <FaBell size={20} />
                  {unreadCount > 0 && (
                    <Badge
                      pill
                      bg="danger"
                      className="position-absolute top-0 start-100 translate-middle border border-light"
                      style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                      <span className="visually-hidden">unread notifications</span>
                    </Badge>
                  )}
                </Button>

                {/* Notification Dropdown Menu */}
                <Dropdown.Menu
                  show={showNotifications}
                  className="shadow mt-2 notification-dropdown"
                  style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
                >
                  <Dropdown.Header className="d-flex justify-content-between align-items-center">
                    Notifications
                    {unreadCount > 0 && (
                      <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                        Mark all read
                      </Button>
                    )}
                  </Dropdown.Header>
                  <Dropdown.Divider className="my-1" />
                  {loadingNotifications ? (
                    <div className="text-center p-3">
                      <Spinner animation="border" size="sm" variant="primary" /> Loading...
                    </div>
                  ) : notifications.length > 0 ? (
                    <ListGroup variant="flush">
                      {notifications.map(notif => (
                        <ListGroup.Item
                          key={notif.id}
                          action={!notif.is_read}
                          onClick={() => handleMarkAsRead(notif.id)}
                          style={{
                            cursor: !notif.is_read ? 'pointer' : 'default',
                            backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                            borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                            padding: '0.75rem 1.25rem',
                            borderBottom: '1px solid #eee'
                          }}
                          className="notification-item"
                        >
                          <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                          <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                          <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <Dropdown.ItemText className="text-center text-muted p-3">
                      You're all caught up!
                    </Dropdown.ItemText>
                  )}
                </Dropdown.Menu>
              </div>

              <div className="agent-user-profile">
                <div className="agent-avatar">
                  <FaUser />
                </div>
                <div className="agent-user-info">
                  <span className="agent-user-name">
                    {currentUser?.name || 'Agent User'}
                  </span>
                  <button className="agent-logout-btn" onClick={handleLogout}>
                    <FaSignOutAlt size={14} />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            </div>
          </header>
          <div className="agent-main-content">
            <Container fluid className="d-flex justify-content-center align-items-center earnings-loading" style={{height: '80vh'}}>
              <div className="text-center">
                <Spinner animation="border" role="status" className="mb-3" variant="primary">
                  <span className="visually-hidden">Loading Dashboard...</span>
                </Spinner>
                <h5>Loading your earnings dashboard</h5>
                <p className="text-muted">Please wait while we fetch your revenue data...</p>
              </div>
            </Container>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agent-dashboard-layout">
        <AgentSidebar />
        <div className="agent-content-wrapper">
          <header className="agent-header">
            <div className="agent-greeting">
              <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
            </div>

            <div ref={searchRef} className="agent-header-search">
              <div className="search-input-container">
                <FaSearch className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search leads, products, invoices..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (searchResults.length > 0) {
                      setShowSearchResults(true);
                    }
                  }}
                />
                {searchQuery && (
                  <button className="clear-search-btn" onClick={clearSearch}>
                    <FaTimes />
                  </button>
                )}
                {isSearching && (
                  <Spinner 
                    animation="border" 
                    size="sm" 
                    className="search-spinner" 
                  />
                )}
              </div>
              
              {/* Search results dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="search-results-dropdown">
                  <SearchResults 
                    results={searchResults}
                    onResultClick={() => setShowSearchResults(false)}
                  />
                </div>
              )}
            </div>

            <div className="agent-header-actions">
              <div className="agent-header-icon">
                <FaQuestionCircle size={18} />
              </div>
              
              <div ref={notificationRef} className="position-relative agent-notifications me-3">
                <Button
                  variant="link"
                  className="text-secondary p-0 position-relative"
                  onClick={toggleNotifications}
                  id="dropdown-notifications-agent"
                  aria-expanded={showNotifications}
                  title="Notifications"
                >
                  <FaBell size={20} />
                  {unreadCount > 0 && (
                    <Badge
                      pill
                      bg="danger"
                      className="position-absolute top-0 start-100 translate-middle border border-light"
                      style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                      <span className="visually-hidden">unread notifications</span>
                    </Badge>
                  )}
                </Button>

                {/* Notification Dropdown Menu */}
                <Dropdown.Menu
                  show={showNotifications}
                  className="shadow mt-2 notification-dropdown"
                  style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
                >
                  <Dropdown.Header className="d-flex justify-content-between align-items-center">
                    Notifications
                    {unreadCount > 0 && (
                      <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                        Mark all read
                      </Button>
                    )}
                  </Dropdown.Header>
                  <Dropdown.Divider className="my-1" />
                  {loadingNotifications ? (
                    <div className="text-center p-3">
                      <Spinner animation="border" size="sm" variant="primary" /> Loading...
                    </div>
                  ) : notifications.length > 0 ? (
                    <ListGroup variant="flush">
                      {notifications.map(notif => (
                        <ListGroup.Item
                          key={notif.id}
                          action={!notif.is_read}
                          onClick={() => handleMarkAsRead(notif.id)}
                          style={{
                            cursor: !notif.is_read ? 'pointer' : 'default',
                            backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                            borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                            padding: '0.75rem 1.25rem',
                            borderBottom: '1px solid #eee'
                          }}
                          className="notification-item"
                        >
                          <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                          <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                          <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <Dropdown.ItemText className="text-center text-muted p-3">
                      You're all caught up!
                    </Dropdown.ItemText>
                  )}
                </Dropdown.Menu>
              </div>

              <div className="agent-user-profile">
                <div className="agent-avatar">
                  <FaUser />
                </div>
                <div className="agent-user-info">
                  <span className="agent-user-name">
                    {currentUser?.name || 'Agent User'}
                  </span>
                  <button className="agent-logout-btn" onClick={handleLogout}>
                    <FaSignOutAlt size={14} />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            </div>
          </header>
          <div className="agent-main-content">
            <Container fluid className="p-4">
              <Alert variant="danger" className="earnings-error">
                <Alert.Heading>Error Loading Data</Alert.Heading>
                <p>{error}</p>
                <hr />
                <div className="d-flex justify-content-end">
                  <button className="btn btn-outline-danger" onClick={() => window.location.reload()}>
                    Retry
                  </button>
                </div>
              </Alert>
            </Container>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dashboard-layout">
      <AgentSidebar />
      <div className="agent-content-wrapper">
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
          </div>

          <div ref={searchRef} className="agent-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search leads, products, invoices..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
              {isSearching && (
                <Spinner 
                  animation="border" 
                  size="sm" 
                  className="search-spinner" 
                />
              )}
            </div>
            
            {/* Search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                <SearchResults 
                  results={searchResults}
                  onResultClick={() => setShowSearchResults(false)}
                />
              </div>
            )}
          </div>

          <div className="agent-header-actions">
            <div className="agent-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            
            <div ref={notificationRef} className="position-relative agent-notifications me-3">
              <Button
                variant="link"
                className="text-secondary p-0 position-relative"
                onClick={toggleNotifications}
                id="dropdown-notifications-agent"
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light"
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              {/* Notification Dropdown Menu */}
              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown"
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read}
                        onClick={() => handleMarkAsRead(notif.id)}
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid #eee'
                        }}
                        className="notification-item"
                      >
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>

            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUser />
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">
                  {currentUser?.name || 'Agent User'}
                </span>
                <button className="agent-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        <div className="agent-main-content">
          <Container fluid className="earnings-dashboard px-4 pb-5">
            <div className="dashboard-header">
              <div>
                <h1 className="dashboard-title">Earnings Dashboard</h1>
                <p className="dashboard-subtitle">Track your performance and commissions</p>
              </div>
              <div className="dashboard-actions">
                <div className="period-filter">
                  <span className="period-label">Last updated:</span>
                  <span className="period-value">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>

      <Tab.Container id="earnings-tabs" activeKey={activeKey} onSelect={setActiveKey}>
        <Nav variant="tabs" className="dashboard-tabs">
          <Nav.Item>
            <Nav.Link eventKey="overview" className="dashboard-tab">
              <FaChartBar className="tab-icon" /> Overview
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="products" className="dashboard-tab">
              <FaChartPie className="tab-icon" /> Products
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content className="mt-4">
          <Tab.Pane eventKey="overview">
            <Row className="mb-4 g-4">
              <Col md={6} lg={3}>
                <Card className="stat-card">
                  <div className="stat-icon-wrapper bg-blue">
                    <FaRupeeSign className="stat-icon" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Earnings</div>
                    <div className="stat-value">{formatCurrency(summaryStats?.total_earnings)}</div>
                    <div className="stat-trend positive">
                      <FaArrowUp className="trend-icon" /> <span>12.4% from last month</span>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="stat-card">
                  <div className="stat-icon-wrapper bg-amber">
                    <FaFileInvoiceDollar className="stat-icon" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Pending Payouts</div>
                    <div className="stat-value">{formatCurrency(summaryStats?.pending_payouts)}</div>
                    <div className="stat-trend negative">
                      <FaArrowDown className="trend-icon" /> <span>3.2% from last month</span>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="stat-card">
                  <div className="stat-icon-wrapper bg-emerald">
                    <FaPercentage className="stat-icon" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Avg. Commission</div>
                    <div className="stat-value">{formatPercentage(summaryStats?.average_commission)}</div>
                    <div className="stat-trend positive">
                      <FaArrowUp className="trend-icon" /> <span>1.8% from last month</span>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="stat-card">
                  <div className="stat-icon-wrapper bg-indigo">
                    <FaUsers className="stat-icon" />
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Leads</div>
                    <div className="stat-value">{summaryStats?.total_leads || 0}</div>
                    <div className="stat-badge">
                      Top product: <Badge bg="light" text="dark">{summaryStats?.top_product_name || 'N/A'}</Badge>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            <Row className="g-4">
              <Col lg={6}>
                <Card className="chart-card h-100">
                  <Card.Header className="chart-header">
                    <h5 className="chart-title">
                      <FaChartBar className="me-2" /> Monthly Earnings Trend
                    </h5>
                    <div className="chart-actions">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Monthly earnings over the last 6 months</Tooltip>}
                      >
                        <span><FaInfoCircle /></span>
                      </OverlayTrigger>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="chart-container" style={{ height: '350px' }}>
                      {monthlyData && monthlyData.labels?.length > 0 ? (
                        <Line options={monthlyChartOptions} data={getMonthlyChartData} />
                      ) : (
                        <div className="no-data-message">Not enough data for monthly chart</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card className="chart-card h-100">
                  <Card.Header className="chart-header">
                    <h5 className="chart-title">
                      <FaChartPie className="me-2" /> Product Breakdown
                    </h5>
                    <div className="chart-actions">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Earnings distribution by product</Tooltip>}
                      >
                        <span><FaInfoCircle /></span>
                      </OverlayTrigger>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="chart-container" style={{ height: '350px', position: 'relative' }}>
                      {productData && productData.length > 0 ? (
                        <Doughnut options={productChartOptions} data={productChartData} />
                      ) : (
                        <div className="no-data-message">No product earnings data available</div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="products">
            <Row className="mb-4">
              <Col>
                <Card className="table-card">
                  <Card.Header className="chart-header">
                    <h5 className="chart-title">Product Performance</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-products">
                        <thead className="table-header">
                          <tr>
                            <th>Product</th>
                            <th>Total Earnings</th>
                            <th>Commission %</th>
                            <th>Leads</th>
                            <th>Conversions</th>
                            <th>Performance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productData && productData.length > 0 ? (
                            productData
                              .sort((a, b) => (b?.total_amount || 0) - (a?.total_amount || 0))
                              .map((product, index) => (
                                <tr key={index}>
                                  <td>
                                    <div className="category-name">
                                      <div 
                                        className="category-indicator" 
                                        style={{ 
                                          backgroundColor: 
                                            index % 10 === 0 ? '#3b82f6' : 
                                            index % 10 === 1 ? '#6366f1' : 
                                            index % 10 === 2 ? '#8b5cf6' : 
                                            index % 10 === 3 ? '#ec4899' : 
                                            index % 10 === 4 ? '#f43f5e' :
                                            index % 10 === 5 ? '#f97316' :
                                            index % 10 === 6 ? '#f59e0b' :
                                            index % 10 === 7 ? '#10b981' :
                                            index % 10 === 8 ? '#06b6d4' :
                                            '#0ea5e9'
                                        }}
                                      ></div>
                                      {product?.category_name || 'Unknown Product'}
                                    </div>
                                  </td>
                                  <td>{formatCurrency(product?.total_amount)}</td>
                                  <td>{formatPercentage(product?.commission_percentage || 0)}</td>
                                  <td>{product?.total_leads || 0}</td>
                                  <td>{product?.conversions || 0}</td>
                                  <td>
                                    <div className="percentage-bar-container">
                                      <div 
                                        className="percentage-bar" 
                                        style={{ 
                                          width: `${product?.conversion_rate || 0}%`,
                                          backgroundColor: 
                                            (product?.conversion_rate || 0) > 70 ? '#10b981' :
                                            (product?.conversion_rate || 0) > 40 ? '#3b82f6' :
                                            (product?.conversion_rate || 0) > 20 ? '#f59e0b' : 
                                            '#ef4444'
                                        }}
                                      ></div>
                                      <span>{formatPercentage(product?.conversion_rate || 0)}</span>
                                    </div>
                                  </td>
                                </tr>
                              ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">No product earnings data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default AgentEarnings;